import { toast } from 'react-toastify';
import { SpaceKind } from 'shared';

import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { HttpException } from '../../../exceptions/index';
import { useAppMutation, useInvalidateQueries, useLocale } from '../../../hooks/index';
import { ApiService } from '../../../services/index';
import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import { SidebarTreeStore } from '../store/sidebar-tree.store';

type Props = {
  onSuccess?: () => void;
};

export const useDeleteWorkspace = ({ onSuccess }: Props = {}) => {
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const { messagesLocale } = useLocale();
  // Not the workspace's own detail: the delete modal still reads it while it closes, and refreshing
  // it would ask the server for the workspace just deleted — a 404. The entry is dropped once unused.
  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_SPACES_HOME,
    QueryKey.GET_SPACES_NAVIGATION,
  );

  const { isPending, mutate: deleteWorkspace } = useAppMutation<
    void,
    HttpException,
    { id: string }
  >([QueryKey.DELETE_WORKSPACE, companyId], {
    mutationFn: ({ id }) =>
      ApiService.delete({ endpoint: BackendRoute.WORKSPACES, routePath: [id] }),
    onSuccess: (_, { id }) => {
      const { resetSelection, selection } = SidebarTreeStore.getState();

      // The open workspace is gone; staying on it would only ask the server for a 404.
      if (selection?.space.kind === SpaceKind.WORKSPACE && selection.space.id === id) {
        resetSelection();
      }

      toast.success(messagesLocale['WORKSPACE_DELETED']);
      invalidateQuery();
      onSuccess?.();
    },
  });

  return { deleteWorkspace, isPending };
};
